import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthContext';
import { connect, type LiveEvent, type LiveStreamStatus } from '../services/liveEventsService';

/**
 * ONE shared `/api/live/events` connection for the whole authenticated app.
 *
 * Mounted once in App.tsx around the routes (Layout/TopBar re-mount on every
 * page change, so they cannot own the socket). The stream is only opened while
 * a user is logged in; public pages (login, signup, …) never connect.
 * Pages and the TopBar subscribe per event type and receive the decoded payload.
 */

export type LiveEventHandler<T = any> = (data: T, event: LiveEvent<T>) => void;

/** `idle` = no connection wanted (logged out); the rest mirrors the stream. */
export type LiveConnectionStatus = LiveStreamStatus | 'idle';

/**
 * Deliberately small: every consumer re-renders when this value changes, so
 * it only carries what components render from. The resume cursor stays in a
 * ref inside the provider — an id per event in here would re-render every
 * subscriber on every event. `subscribe` is referentially stable.
 */
export interface LiveEventsContextValue {
  status: LiveConnectionStatus;
  /** Subscribe to one event type (`'*'` for all). Returns the unsubscribe function. */
  subscribe: <T = any>(type: string, handler: LiveEventHandler<T>) => () => void;
}

const noop = () => {};

/** Safe default so components render (without live updates) outside the provider, e.g. in tests. */
export const LIVE_EVENTS_FALLBACK: LiveEventsContextValue = {
  status: 'idle',
  subscribe: () => noop,
};

const LiveEventsContext = createContext<LiveEventsContextValue>(LIVE_EVENTS_FALLBACK);

export const LiveEventsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [status, setStatus] = useState<LiveConnectionStatus>('idle');
  /** Highest event id seen — what a reconnect of this session resumes from. */
  const lastIdRef = useRef<number | null>(null);
  const handlersRef = useRef(new Map<string, Set<LiveEventHandler>>());

  const subscribe = useCallback(<T,>(type: string, handler: LiveEventHandler<T>) => {
    const registry = handlersRef.current;
    const set = registry.get(type) ?? new Set<LiveEventHandler>();
    set.add(handler as LiveEventHandler);
    registry.set(type, set);
    return () => {
      const current = registry.get(type);
      if (!current) return;
      current.delete(handler as LiveEventHandler);
      if (current.size === 0) registry.delete(type);
    };
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      // Logged out: the next session (maybe another account) must not resume
      // from this one's cursor.
      lastIdRef.current = null;
      setStatus('idle');
      return undefined;
    }

    const dispatch = (event: LiveEvent) => {
      const registry = handlersRef.current;
      // Snapshot: a handler may unsubscribe while we iterate.
      const targets = [
        ...Array.from(registry.get(event.type) ?? []),
        ...Array.from(registry.get('*') ?? []),
      ];
      for (const handler of targets) {
        try {
          handler(event.data, event);
        } catch (error) {
          console.error(`Live event handler for "${event.type}" failed:`, error);
        }
      }
    };

    const disconnect = connect({
      after: lastIdRef.current,
      onStatus: setStatus,
      onEvent: (event) => {
        if (event.id != null) lastIdRef.current = event.id;
        dispatch(event);
      },
    });
    return disconnect;
  }, [isAuthenticated]);

  const value = useMemo<LiveEventsContextValue>(() => ({ status, subscribe }), [status, subscribe]);

  return <LiveEventsContext.Provider value={value}>{children}</LiveEventsContext.Provider>;
};

export function useLiveEventsContext(): LiveEventsContextValue {
  return useContext(LiveEventsContext);
}

export default LiveEventsContext;
